import type { LucideIcon } from "lucide-react";
import {
 Droplets,
 Flame,
 Home,
 Leaf,
 Sliders,
 Sun,
 Wind,
 Wrench,
} from "lucide-react";

export type CommercialSectorSlug =
 | "councils"
 | "housing-associations"
 | "facilities-management"
 | "main-contractors"
 | "landlords"
 | "insurers";

export interface ScopeMeasure {
 icon: LucideIcon;
 title: string;
 scope: string;
 accred: string;
 href: string;
 secondHref?: { label: string; href: string };
}

export interface CommercialSectorContent {
 slug: CommercialSectorSlug;
 path: string;
 name: string;
 formSector: string;
 valueProposition: string;
 metaTitle: string;
 metaDescription: string;
 scopeMeasures: ScopeMeasure[];
}

const MEASURES = {
 damp: {
 icon: Droplets,
 title: "Damp, Mould & Structural Remediation",
 scope: "Diagnostics-led response to damp, mould and structural defects. Insurance-backed guarantee.",
 accred: "Inspection-led · Insurance-Backed Guarantee",
 href: "/damp-survey",
 },
 ventilation: {
 icon: Wind,
 title: "Ventilation & Extraction",
 scope: "Whole-house ventilation strategy as part of condensation-and-mould response.",
 accred: "PAS 2030 · Whole-House Assessment",
 href: "/ventilation-extraction",
 },
 loftIwi: {
 icon: Home,
 title: "Loft & Internal Wall Insulation",
 scope: "Retrofit fabric upgrades for EPC uplift and decarbonisation programmes.",
 accred: "PAS 2030 · TrustMark · Insurance-Backed Guarantee",
 href: "/loft-insulation",
 secondHref: { label: "Internal Wall Insulation", href: "/internal-wall-insulation" },
 },
 wallTies: {
 icon: Wrench,
 title: "Remedial Wall Ties & Structural",
 scope: "Borescope-evidenced tie replacement using tested, CE-marked systems.",
 accred: "Structural · Tested Systems · Fully Insured",
 href: "/remedial-wall-ties",
 },
 boilers: {
 icon: Flame,
 title: "Boilers, Heating Controls & LGSR",
 scope: "Boiler replacement, controls upgrades, and landlord gas safety (LGSR / CP12).",
 accred: "Gas Safe Registered · Manufacturer-Backed Warranty",
 href: "/boilers-heating",
 secondHref: { label: "Heating Controls", href: "/heating-controls" },
 },
 heatingControls: {
 icon: Sliders,
 title: "Heating Controls",
 scope: "Zoning, TRVs and smart controls for better comfort and lower running costs.",
 accred: "Gas Safe Registered · Manufacturer-Backed",
 href: "/heating-controls",
 },
 ashp: {
 icon: Leaf,
 title: "Air Source Heat Pumps",
 scope: "Fabric-first heat pump programmes for decarbonisation targets.",
 accred: "MCS Certified · Full Heat-Loss Survey",
 href: "/air-source-heat-pumps",
 },
 commercialSolar: {
 icon: Sun,
 title: "Commercial Solar PV",
 scope: "B2B procurement-evidence delivery for commercial estates and HA portfolios.",
 accred: "MCS · Commercial G99 / DNO · Framework-Ready",
 href: "/commercial-solar-pv",
 },
} as const;

function pick(...keys: (keyof typeof MEASURES)[]): ScopeMeasure[] {
 return keys.map((key) => {
 const m = MEASURES[key];
 return {
 icon: m.icon,
 title: m.title,
 scope: m.scope,
 accred: m.accred,
 href: m.href,
 ...("secondHref" in m && m.secondHref ? { secondHref: m.secondHref } : {}),
 };
 });
}

export const COMMERCIAL_SECTORS: CommercialSectorContent[] = [
 {
 slug: "councils",
 path: "/commercial-sectors/councils",
 name: "Councils",
 formSector: "Council",
 valueProposition:
 "Estate and operational programmes with social-value and KPI reporting, framework-ready delivery across retrofit, heating and remedial works.",
 metaTitle: "Councils | Retrofit, Heating & Remedial Delivery | CRS Energy",
 metaDescription:
 "Framework-ready retrofit, heating and remedial delivery for councils across Wales and the South West. Social-value and KPI reporting, RAMS and accreditations to tender standard.",
 scopeMeasures: pick(
 "damp",
 "ventilation",
 "loftIwi",
 "wallTies",
 "boilers",
 "ashp",
 "commercialSolar"
 ),
 },
 {
 slug: "housing-associations",
 path: "/commercial-sectors/housing-associations",
 name: "Housing Associations",
 formSector: "Housing Association",
 valueProposition:
 "Portfolio retrofit and hazard response aligned to WHQS, coordinated programmes with completion reporting and tenant liaison.",
 metaTitle: "Housing Associations | Retrofit, Heating & Remedial Delivery | CRS Energy",
 metaDescription:
 "Portfolio retrofit and hazard response for housing associations. WHQS-aligned delivery across damp, ventilation, insulation, heating and decarbonisation programmes.",
 scopeMeasures: pick("damp", "ventilation", "loftIwi", "wallTies", "boilers", "ashp"),
 },
 {
 slug: "facilities-management",
 path: "/commercial-sectors/facilities-management",
 name: "Facilities Management",
 formSector: "Facilities Management",
 valueProposition:
 "Planned and reactive works coordinated with maintenance windows, single point of contact from survey through to handover.",
 metaTitle: "Facilities Management | Retrofit, Heating & Remedial Delivery | CRS Energy",
 metaDescription:
 "Planned and reactive retrofit, heating and remedial works for facilities managers. Coordinated with maintenance windows, RAMS and programme reporting.",
 scopeMeasures: pick("boilers", "heatingControls", "ventilation", "damp", "wallTies", "commercialSolar"),
 },
 {
 slug: "main-contractors",
 path: "/commercial-sectors/main-contractors",
 name: "Main Contractors",
 formSector: "Main Contractor",
 valueProposition:
 "Subcontract M&E and renewables to programme with RAMS, ITPs and snagging, framework-grade delivery on tier-one sites.",
 metaTitle: "Main Contractors | Retrofit, Heating & Remedial Delivery | CRS Energy",
 metaDescription:
 "Subcontract M&E and renewables for main contractors. Programme-aligned delivery with RAMS, ITPs, snagging discipline and documented handover.",
 scopeMeasures: pick("boilers", "ashp", "heatingControls", "ventilation", "loftIwi", "wallTies", "commercialSolar"),
 },
 {
 slug: "landlords",
 path: "/commercial-sectors/landlords",
 name: "Landlords",
 formSector: "Landlord",
 valueProposition:
 "Compliance, gas safety and damp/mould response, landlord gas safety (LGSR / CP12), void works and completion documentation.",
 metaTitle: "Landlords | Retrofit, Heating & Remedial Delivery | CRS Energy",
 metaDescription:
 "Compliance-focused delivery for landlords and managing agents. Gas safety (LGSR / CP12), damp and mould response, ventilation and structural remedials.",
 scopeMeasures: pick("boilers", "heatingControls", "ventilation", "damp", "wallTies"),
 },
 {
 slug: "insurers",
 path: "/commercial-sectors/insurers",
 name: "Insurers",
 formSector: "Insurer",
 valueProposition:
 "Single-property structural and damp claims, evidenced and closed out to the standard your loss adjusters expect first time.",
 metaTitle: "Insurers | Claims-Led Damp & Structural Remediation | CRS Energy",
 metaDescription:
 "Single-property damp and structural claim remediation for insurers. Cause evidenced before remedy, borescope and moisture records, post-works verification.",
 scopeMeasures: pick("damp", "ventilation", "wallTies", "loftIwi"),
 },
];

export function getCommercialSector(slug: string): CommercialSectorContent | undefined {
 return COMMERCIAL_SECTORS.find((s) => s.slug === slug);
}

export const COMMERCIAL_SECTOR_SLUGS = COMMERCIAL_SECTORS.map((s) => s.slug);

export const SECTOR_PAGE_LINKS = COMMERCIAL_SECTORS.map((s) => ({
 name: s.name,
 path: s.path,
 slug: s.slug,
}));
